import React, { useState, useEffect, useRef } from 'react';
import { Calculator, Plus, Trash2, Trophy, Clock, Users, Settings, Play, Square, Download } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import UpgradePrompt from '../components/UpgradePrompt';

export default function ScoreSimulator() {
  const { hasFeature, isLoading } = useSubscription();
  const [eventType, setEventType] = useState('straight'); // 'straight' or 'divisional'
  const [timeSplits, setTimeSplits] = useState([0.5, 1, 1.5, 2]); // For divisional events
  const [contestants, setContestants] = useState([]);
  const [newContestant, setNewContestant] = useState({ name: '', time: '' });
  const [eventName, setEventName] = useState('');
  
  // Timer state
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerIntervalRef = useRef(null);

  // Show loading state while checking subscription
  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-kci-600">Loading...</div>
        </div>
      </div>
    );
  }

  // Show upgrade prompt if user doesn't have access
  if (!hasFeature('score_simulator')) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <UpgradePrompt 
          featureName="Score Simulator"
          description="Simulate rodeo event scoring with real-time placement calculations for both straight time and divisional events."
          features={[
            "Real-time placement calculations",
            "Support for straight time events (bronc riding, bull riding, pole bending)",
            "Support for divisional events (barrel racing with time splits)",
            "Mobile-responsive design",
            "Export and save results"
          ]}
        />
      </div>
    );
  }

  // Calculate divisions and placements
  const calculateResults = () => {
    if (contestants.length === 0) return [];

    const validContestants = contestants.filter(c => c.name.trim() && c.time && !isNaN(parseFloat(c.time)));
    
    if (validContestants.length === 0) return [];

    // Sort by time (ascending - fastest first)
    const sortedContestants = [...validContestants].sort((a, b) => parseFloat(a.time) - parseFloat(b.time));
    
    if (eventType === 'straight') {
      // Straight time events - just rank by time
      return sortedContestants.map((contestant, index) => ({
        ...contestant,
        place: index + 1,
        division: null
      }));
    } else {
      // Divisional events - calculate divisions based on best time + splits
      const bestTime = parseFloat(sortedContestants[0].time);
      const divisions = [];
      
      // Calculate division boundaries
      let currentBoundary = bestTime;
      for (let i = 0; i < timeSplits.length; i++) {
        const nextBoundary = currentBoundary + timeSplits[i];
        divisions.push({
          division: i + 1,
          minTime: currentBoundary,
          maxTime: nextBoundary
        });
        currentBoundary = nextBoundary;
      }
      
      // Add final division (everything above last split)
      divisions.push({
        division: timeSplits.length + 1,
        minTime: currentBoundary,
        maxTime: Infinity
      });
      
      // Assign contestants to divisions and calculate placements within each division
      const results = [];
      const divisionContestants = {};
      
      // Group contestants by division
      sortedContestants.forEach(contestant => {
        const time = parseFloat(contestant.time);
        const division = divisions.find(d => time >= d.minTime && time < d.maxTime);
        
        if (division) {
          if (!divisionContestants[division.division]) {
            divisionContestants[division.division] = [];
          }
          divisionContestants[division.division].push(contestant);
        }
      });
      
      // Calculate placements within each division
      Object.keys(divisionContestants).forEach(divisionNum => {
        const divContestants = divisionContestants[divisionNum].sort((a, b) => parseFloat(a.time) - parseFloat(b.time));
        divContestants.forEach((contestant, index) => {
          results.push({
            ...contestant,
            place: index + 1,
            division: parseInt(divisionNum)
          });
        });
      });
      
      return results.sort((a, b) => {
        if (a.division !== b.division) {
          return a.division - b.division;
        }
        return a.place - b.place;
      });
    }
  };

  const addContestant = () => {
    if (newContestant.name.trim() && newContestant.time && !isNaN(parseFloat(newContestant.time))) {
      setContestants([...contestants, { ...newContestant, time: parseFloat(newContestant.time) }]);
      setNewContestant({ name: '', time: '' });
    }
  };

  const removeContestant = (index) => {
    setContestants(contestants.filter((_, i) => i !== index));
  };

  const addTimeSplit = () => {
    setTimeSplits([...timeSplits, 1.0]);
  };

  const removeTimeSplit = (index) => {
    if (timeSplits.length > 1) {
      setTimeSplits(timeSplits.filter((_, i) => i !== index));
    }
  };

  const updateTimeSplit = (index, value) => {
    const newSplits = [...timeSplits];
    newSplits[index] = parseFloat(value) || 0;
    setTimeSplits(newSplits);
  };

  const clearAll = () => {
    setContestants([]);
    setNewContestant({ name: '', time: '' });
  };

  // Timer functions
  const startTimer = () => {
    if (!isTimerRunning) {
      const startTime = Date.now();
      setTimerStartTime(startTime);
      setIsTimerRunning(true);
      
      timerIntervalRef.current = setInterval(() => {
        const currentTime = Date.now();
        const elapsed = (currentTime - startTime) / 1000; // Convert to seconds
        setElapsedTime(elapsed);
      }, 10); // Update every 10ms for smooth display
    }
  };

  const stopTimer = () => {
    if (isTimerRunning) {
      clearInterval(timerIntervalRef.current);
      setIsTimerRunning(false);
      
      // Format elapsed time to 3 decimal places and update the time input
      const formattedTime = elapsedTime.toFixed(3);
      setNewContestant(prev => ({ ...prev, time: formattedTime }));
      
      // Reset timer state
      setTimerStartTime(null);
      setElapsedTime(0);
    }
  };

  const resetTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    setIsTimerRunning(false);
    setTimerStartTime(null);
    setElapsedTime(0);
    // Also clear the time input when resetting
    setNewContestant(prev => ({ ...prev, time: '' }));
  };

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // CSV Export function
  const exportToCSV = () => {
    if (results.length === 0) return;

    // Create CSV content
    let csvContent = '';
    
    // Add event title
    if (eventName) {
      csvContent += `Event: ${eventName}\n`;
    }
    
    // Add time splits for divisional events
    if (eventType === 'divisional' && timeSplits.length > 0) {
      csvContent += `Time Splits: ${timeSplits.join(', ')} seconds\n`;
    }
    
    // Add empty line
    csvContent += '\n';
    
    // Add headers
    const headers = ['Place', 'Name', 'Time (seconds)'];
    if (eventType === 'divisional') {
      headers.splice(1, 0, 'Division');
    }
    csvContent += headers.join(',') + '\n';
    
    // Add results data
    results.forEach(result => {
      const row = [
        result.place,
        result.name,
        result.time.toFixed(3)
      ];
      if (eventType === 'divisional') {
        row.splice(1, 0, `${result.division}D`);
      }
      csvContent += row.join(',') + '\n';
    });
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const eventNameSafe = eventName ? eventName.replace(/[^a-zA-Z0-9]/g, '_') : 'ScoreSimulator';
    link.setAttribute('download', `${eventNameSafe}_${eventType}_${timestamp}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const results = calculateResults();

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto overflow-x-hidden">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-kci-900 mb-2 flex items-center">
          <Calculator className="mr-2 sm:mr-3 w-6 h-6 sm:w-8 sm:h-8" />
          Score Simulator
        </h1>
        <p className="text-sm sm:text-base text-kci-600">
          Simulate rodeo event scoring with real-time placement calculations
        </p>
      </div>

      {/* Event Configuration */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-kci-900 mb-4 flex items-center">
          <Settings className="mr-2 w-5 h-5" />
          Event Configuration
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-medium text-kci-700 mb-2">
              Event Name
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="e.g., Barrel Racing, Pole Bending"
              className="w-full px-3 py-2 border border-kci-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kci-500 text-sm sm:text-base"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-kci-700 mb-2">
              Event Type
            </label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full px-3 py-2 border border-kci-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kci-500 text-sm sm:text-base"
            >
              <option value="straight">Straight Time Events</option>
              <option value="divisional">Divisional Events</option>
            </select>
          </div>
        </div>

        {/* Time Splits Configuration - Only for divisional events */}
        {eventType === 'divisional' && (
          <div className="mt-4 sm:mt-6 text-center">
            <label className="block text-sm font-medium text-kci-700 mb-2">
              Time Splits (seconds)
            </label>
            <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-3">
              {timeSplits.map((split, index) => (
                <div key={index} className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.1"
                    value={split}
                    onChange={(e) => updateTimeSplit(index, e.target.value)}
                    className="w-14 sm:w-16 md:w-20 px-1 sm:px-2 py-1 border border-kci-300 rounded text-center text-xs sm:text-sm"
                  />
                  {timeSplits.length > 1 && (
                    <button
                      onClick={() => removeTimeSplit(index)}
                      className="text-red-500 hover:text-red-700 p-0.5 sm:p-1"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addTimeSplit}
                className="px-2 py-1 bg-kci-100 text-kci-700 rounded hover:bg-kci-200 flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Add Split</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
            <p className="text-xs sm:text-sm text-kci-500 break-words">
              Example: 0.5, 1, 1.5, 2 creates 5 divisions (1D, 2D, 3D, 4D, 5D)
            </p>
          </div>
        )}
      </div>

      {/* Add Contestant */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-kci-900 mb-4 flex items-center">
          <Users className="mr-2 w-5 h-5" />
          Add Contestant
        </h2>
        
        <div className="space-y-4">
          {/* Contestant Name */}
          <div>
            <input
              type="text"
              placeholder="Contestant Name"
              value={newContestant.name}
              onChange={(e) => setNewContestant({ ...newContestant, name: e.target.value })}
              className="w-full px-3 py-2 border border-kci-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kci-500 text-sm sm:text-base"
            />
          </div>
          
          {/* Time Input and Timer */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full sm:w-48">
                <input
                  type="number"
                  step="0.001"
                  placeholder="Time (seconds)"
                  value={newContestant.time}
                  onChange={(e) => setNewContestant({ ...newContestant, time: e.target.value })}
                  className="w-full px-3 py-2 border border-kci-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kci-500 text-sm sm:text-base"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={isTimerRunning ? stopTimer : startTimer}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm sm:text-base font-medium transition-colors ${
                    isTimerRunning
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isTimerRunning ? (
                    <>
                      <Square className="w-4 h-4" />
                      <span className="hidden sm:inline">Stop Timer</span>
                      <span className="sm:hidden">Stop</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span className="hidden sm:inline">Start Timer</span>
                      <span className="sm:hidden">Start</span>
                    </>
                  )}
                </button>
                <button
                  onClick={resetTimer}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-1 text-sm sm:text-base"
                >
                  <Clock className="w-4 h-4" />
                  <span className="hidden sm:inline">Reset</span>
                </button>
              </div>
            </div>
            
            {/* Timer Display */}
            {(isTimerRunning || elapsedTime > 0) && (
              <div className="bg-kci-50 border border-kci-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-kci-600" />
                    <span className="text-sm font-medium text-kci-700">
                      {isTimerRunning ? 'Timer Running' : 'Last Time'}
                    </span>
                  </div>
                  <div className="text-lg font-mono font-bold text-kci-900">
                    {elapsedTime.toFixed(3)}s
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Add Contestant Button */}
          <div className="flex justify-end">
            <button
              onClick={addContestant}
              className="px-4 py-2 bg-kci-600 text-white rounded-md hover:bg-kci-700 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Contestant</span>
              <span className="sm:hidden">Add Contestant</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
            <div className="flex flex-col">
              <h2 className="text-lg sm:text-xl font-semibold text-kci-900 flex items-center">
                <Trophy className="mr-2 w-5 h-5" />
                Results
              </h2>
              {eventName && (
                <h3 className="text-base sm:text-lg font-medium text-kci-700 mt-1">
                  {eventName}
                </h3>
              )}
              {eventType === 'divisional' && timeSplits.length > 0 && (
                <div className="mt-2">
                  <span className="text-sm text-kci-600">Time Splits: </span>
                  <span className="text-sm font-medium text-kci-800">
                    {timeSplits.map((split, index) => (
                      <span key={index}>
                        {split}s
                        {index < timeSplits.length - 1 && ', '}
                      </span>
                    ))}
                  </span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={exportToCSV}
                className="w-full sm:w-auto px-3 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={clearAll}
                className="w-full sm:w-auto px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            </div>
          </div>

          {/* Mobile Card Layout */}
          <div className="block sm:hidden space-y-3">
            {results.map((result, index) => (
              <div key={index} className="bg-kci-50 rounded-lg p-4 border border-kci-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-kci-100 text-kci-800 rounded-full text-xs font-medium">
                      {result.place}
                    </span>
                    {eventType === 'divisional' && (
                      <span className="inline-flex items-center px-2 py-1 bg-rodeo-100 text-rodeo-800 rounded text-xs font-medium">
                        {result.division}D
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeContestant(contestants.findIndex(c => c.name === result.name && c.time === result.time))}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-sm font-medium text-kci-900 mb-1">{result.name}</div>
                <div className="text-sm text-kci-600 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {result.time.toFixed(3)}s
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-kci-200">
                  <th className="text-left py-3 px-4 font-medium text-kci-700 text-sm">Place</th>
                  {eventType === 'divisional' && (
                    <th className="text-left py-3 px-4 font-medium text-kci-700 text-sm">Division</th>
                  )}
                  <th className="text-left py-3 px-4 font-medium text-kci-700 text-sm">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-kci-700 text-sm">Time</th>
                  <th className="text-left py-3 px-4 font-medium text-kci-700 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={index} className="border-b border-kci-100 hover:bg-kci-50">
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-kci-100 text-kci-800 rounded-full text-sm font-medium">
                        {result.place}
                      </span>
                    </td>
                    {eventType === 'divisional' && (
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-1 bg-rodeo-100 text-rodeo-800 rounded text-sm font-medium">
                          {result.division}D
                        </span>
                      </td>
                    )}
                    <td className="py-3 px-4 font-medium text-kci-900">{result.name}</td>
                    <td className="py-3 px-4 text-kci-600 flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {result.time.toFixed(3)}s
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => removeContestant(contestants.findIndex(c => c.name === result.name && c.time === result.time))}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Stats */}
          <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-kci-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-kci-600">Total Contestants</div>
              <div className="text-xl sm:text-2xl font-bold text-kci-900">{results.length}</div>
            </div>
            <div className="bg-kci-50 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-kci-600">Best Time</div>
              <div className="text-xl sm:text-2xl font-bold text-kci-900">
                {results.length > 0 ? `${results[0].time.toFixed(3)}s` : 'N/A'}
              </div>
            </div>
            {eventType === 'divisional' && (
              <div className="bg-kci-50 p-3 sm:p-4 rounded-lg sm:col-span-2 lg:col-span-1">
                <div className="text-xs sm:text-sm text-kci-600">Divisions</div>
                <div className="text-xl sm:text-2xl font-bold text-kci-900">
                  {new Set(results.map(r => r.division)).size}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 sm:mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">How to Use</h3>
        <ul className="text-blue-800 space-y-1 text-sm sm:text-base break-words">
          <li>• <strong>Straight Time Events:</strong> Contestants are ranked by fastest time (1st, 2nd, 3rd, etc.)</li>
          <li>• <strong>Divisional Events:</strong> Contestants are placed into divisions based on time splits from the best time</li>
          <li>• <strong>Time Splits:</strong> Each split represents the time difference between divisions (e.g., 2.0 seconds between 1D and 2D)</li>
          <li>• <strong>Real-time Updates:</strong> Results automatically recalculate when you add or remove contestants</li>
        </ul>
      </div>
    </div>
  );
}
